import { createSignal, type Component } from 'solid-js';
import { cn } from '~/lib/utils';

export interface RangeSliderProps {
  value: number; // 0–100
  onValueChange: (value: number) => void;
  disabled?: boolean;
  class?: string;
  trackClass?: string;
  activeClass?: string;
}

export const RangeSlider: Component<RangeSliderProps> = (props) => {
  let trackEl: HTMLDivElement | undefined;
  const [isDragging, setIsDragging] = createSignal(false);

  const percentValue = () => Math.max(0, Math.min(100, props.value));

  function getVal(e: MouseEvent): number {
    if (!trackEl) return 0;
    const rect = trackEl.getBoundingClientRect();
    return Math.round(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * 100);
  }

  function startDrag(e: MouseEvent): void {
    if (props.disabled) return;
    e.preventDefault();
    setIsDragging(true);
    const emit = (ev: MouseEvent) => props.onValueChange(getVal(ev));
    emit(e);
    const up = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', emit);
      document.removeEventListener('mouseup', up);
    };
    document.addEventListener('mousemove', emit);
    document.addEventListener('mouseup', up);
  }

  return (
    <div
      ref={(el) => (trackEl = el)}
      class={cn(
        'relative h-4 flex items-center select-none',
        props.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        props.class
      )}
      onMouseDown={startDrag}
    >
      <div
        class={cn(
          'absolute inset-x-0 h-1 bg-background/60 rounded-full pointer-events-none',
          props.trackClass
        )}
      >
        <div
          class={cn(
            'absolute left-0 h-full bg-primary rounded-full pointer-events-none',
            props.activeClass
          )}
          style={{ width: `${percentValue()}%` }}
        />
      </div>
      <div
        class={cn(
          'absolute size-3.5 rounded-full bg-surface-foreground border border-accent shadow-md pointer-events-none',
          isDragging() && 'scale-110'
        )}
        style={{
          left: `${percentValue()}%`,
          top: '50%',
          transform: isDragging()
            ? 'translateX(-45%) translateY(-45%)'
            : 'translateX(-50%) translateY(-50%)',
        }}
      />
    </div>
  );
};
